import React, { Component, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'

import AutoFocus from '../../AutoFocus'
import ImagePopup from '../ImagePopup'

import iconStyle from 'Styles/icon.css'
import style from './style.css'
import sharedStyle from '../sharedStyle.css'

import xIcon from 'Assets/x-icon-red.svg'
import checkIcon from 'Assets/check-green.svg'

import replaceUnderscores from '../../../lib/replaceUnderscores'

export function AnswerChoice (props) {
  let correctnessClass
  let radioButton
  let bgClass = ''
  let inputDisabled = false
  const [inputFocused, setInputFocused] = useState(false)

  function onKeyDown (event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.target.click(event)
    }
  }

  if (props.nukeBg) {
    bgClass = style.nukeBg
  }

  if ((props.isAnswered || props.isComplete) && props.isCorrect) {
    correctnessClass = style.correct
    radioButton = (
      <img
        className={classnames(iconStyle.small, style.answerButtonCorrect)}
        src={checkIcon}
        alt={'Correct: ' + props.label}
      />
    )
    inputDisabled = true
  } else if (props.isAnswered && !props.isCorrect) {
    correctnessClass = style.incorrect
    radioButton = (
      <img
        className={classnames(iconStyle.small, style.answerButtonIncorrect)}
        src={xIcon}
        alt={'Incorrect: ' + props.label}
      />
    )
    inputDisabled = true
  } else if (props.isRemoved) {
    correctnessClass = style.removed
    radioButton = (
      <span
        className={classnames(style.answerButtonBase, style.answerButtonRemoved)}
      />
    )
  } else if (props.isComplete) {
    correctnessClass = style.completedQuestion
    radioButton = (
      <span
        className={classnames(style.answerButtonBase, style.answerButtonRemoved)}
        data-test-id="multipleChoiceAnswer"
      />
    )
  } else {
    correctnessClass = ''
    radioButton = (
      <span
        className={classnames(style.answerButtonBase, style.answerButtonOpen,
          { [style.completedRadio]: props.isComplete })}
      />
    )
  }

  const isDisabled = props.isComplete || props.isRemoved || inputDisabled

  return (
    <label
      className={classnames(style.answerChoice,
        bgClass, correctnessClass, { [style.completedQuestion]: props.isComplete, [style.focusedAnswerChoice]: inputFocused })}
      data-test-id="multipleChoiceAnswer"
    >
      <input
        name="multipleChoice"
        type="radio"
        value={props.value}
        disabled={isDisabled}
        onClick={(event) => {
          props.onClick(event)
        }}
        onKeyDown={(event) => {
          onKeyDown(event)
        }} // This allows deselecting with the keyboard. We should remove it, once we have a clear button
        id={`multipleChoiceOption${props.idx}`}
        checked={!isDisabled ? props.checked : undefined}
        onFocus={() => {
          setInputFocused(true)
        }}
        onBlur={() => {
          setInputFocused(false)
        }}
      />
      {radioButton}
      <div
        className={classnames(style.answerText, { [style.answerTextUnselected]: props.isComplete && !props.isCorrect })}
        dangerouslySetInnerHTML={{ __html: props.label }}
        htmlFor={`multipleChoiceOption${props.idx}`}
      />
    </label>
  )
}

// tdoug - I don't think these are read since nothing is trigged with bad props validation, can probably be deleted
AnswerChoice.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  isCorrect: PropTypes.bool.isRequired,
  isAnswered: PropTypes.bool.isRequired,
  isRemoved: PropTypes.bool.isRequired,
  isComplete: PropTypes.bool.isRequired,
  nukeBg: PropTypes.bool,
  checked: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  idx: PropTypes.number
}

export default class MultipleChoice extends Component {
  constructor (props) {
    super(props)

    this.state = {
      currentlySelectedInput: null
    }

    this.onAnswer = this.onAnswer.bind(this)
    this.inputFormRef = React.createRef();
  }

  componentDidMount() {
    this.inputFormRef.current.addEventListener('focusin', this.handleFocusIn);
  }

  componentWillUnmount() {
    this.inputFormRef.current.removeEventListener('focusin', this.handleFocusIn);
  }

  handleFocusIn = (event) => {
    const target = event.target;
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  onAnswer (event) {
    if (this.state.currentlySelectedInput === event.target) {
      event.target.checked = false
      this.setState({
        currentlySelectedInput: null
      })
      this.props.onAnswer(null)
    } else {
      this.setState({
        currentlySelectedInput: event.target
      })
      this.props.onAnswer(event.target.value)
    }
  }

  render () {
    const question = this.props.question
    if (_.isEmpty(question)) {
      return null
    }

    return (
      <div data-test-id="MultipleChoice">
        <AutoFocus>
          <ImagePopup>
            <span className={classnames('sr-only', sharedStyle.selectPrompt)}>
              Please choose the correct answer from the following choices, and then
              select the submit answer button.
            </span>
          </ImagePopup>
        </AutoFocus>
        <form className={style.answerContainer} ref={this.inputFormRef}>
          <fieldset>
            <legend>
              <h2
                className={sharedStyle.prompt}
                dangerouslySetInnerHTML={{ __html: replaceUnderscores(question.prompt) }}
                data-test-id="activityQuestion"
                ref={(header) => { this.questionHeader = header }}
              />
            </legend>
            {
              _.map(question.choices, (choice, idx) => {
                return (
                  <AnswerChoice
                    key={choice.value}
                    value={choice.value}
                    label={choice.label}
                    isCorrect={choice.isCorrect}
                    isAnswered={choice.isAnswered}
                    isRemoved={choice.isRemoved}
                    isComplete={question.isComplete}
                    onClick={this.onAnswer}
                    idx={idx}
                    checked={this.state.currentlySelectedInput?.value === `${choice.value}`}
                  />
                )
              })
            }
          </fieldset>
        </form>
      </div>
    )
  }
}

MultipleChoice.propTypes = {
  /**
   * @param {Number}
   */
  onAnswer: PropTypes.func.isRequired,
  question: PropTypes.shape({
    prompt: PropTypes.string.isRequired,
    isComplete: PropTypes.bool.isRequired,
    choices: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired, // value passed to onAnswer
        label: PropTypes.string.isRequired,
        isAnswered: PropTypes.bool.isRequired,
        isCorrect: PropTypes.bool.isRequired,
        isRemoved: PropTypes.bool.isRequired
      })
    ).isRequired
  }).isRequired
}
